/**
 * Releases Page
 *
 * Main list page for Release QA runs
 */

import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRowLinkBuilder } from '../../lib/useRowLink';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page,
  PageHeader,
  DataTable,
  LoadingOrbit,
  Text,
  Button,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { GitBranch, Plus } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useReleaseRuns } from '../../features/releases/useReleases';
import type { ReleaseRunListItem, ReleaseQARunStatus } from '../../features/releases/releaseTypes';
import {
  getReleaseRunStatusMeta,
  getTriggerSourceLabel,
} from '../../features/releases/releaseTypes';
import { TriggerReleaseDialog } from '../../features/releases/components/TriggerReleaseDialog';

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

const ReleaseStatusBadge: React.FC<{ status: ReleaseQARunStatus }> = ({ status }) => {
  const meta = getReleaseRunStatusMeta(status);
  const variantMap: Record<
    string,
    'success' | 'secondary' | 'warning' | 'destructive' | 'outline'
  > = {
    green: 'success',
    blue: 'secondary',
    yellow: 'warning',
    red: 'destructive',
    gray: 'outline',
  };
  return (
    <TagBadge
      name={meta.label}
      color={badgeVariantToTagColor(variantMap[meta.color] ?? 'outline')}
      hideDot
    />
  );
};

export const Releases: React.FC = () => {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const search = useSearch({ from: '/releases' });
  const page = (search as { page?: number }).page ?? 1;
  const pageSize = (search as { pageSize?: number }).pageSize ?? 20;
  const propertyId = (search as { propertyId?: string }).propertyId;

  const [triggerDialogOpen, setTriggerDialogOpen] = React.useState(false);

  useBreadcrumbs([{ key: 'releases', label: 'Releases' }]);

  const runsQuery = useReleaseRuns({ page, pageSize, propertyId });

  const columns: ColumnDef<StockFeatures, ReleaseRunListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <ReleaseStatusBadge status={row.original.status} />,
        meta: { flex: 1 },
      },
      {
        id: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <Text size="sm" className="truncate">
            {row.original.title || row.original.propertyName}
          </Text>
        ),
        meta: { flex: 2 },
      },
      {
        accessorKey: 'propertyName',
        header: 'Application',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground truncate">
            {row.original.propertyName}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        accessorKey: 'triggerSource',
        header: 'Trigger',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {getTriggerSourceLabel(row.original.triggerSource)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        id: 'testers',
        header: 'Testers',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.testersCompleted}/{row.original.testersSpawned}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'issuesFound',
        header: 'Issues',
        cell: ({ row }) => (
          <Text
            size="sm"
            className={row.original.issuesFound > 0 ? 'text-destructive' : 'text-muted-foreground'}
          >
            {row.original.issuesFound}
          </Text>
        ),
        meta: { flex: 0.6 },
      },
      {
        id: 'regressions',
        header: 'Regressions',
        cell: ({ row }) => {
          const total = row.original.regressionTestsTotal;
          const passed = row.original.regressionTestsPassed;
          if (total === 0)
            return (
              <Text size="sm" className="text-muted-foreground">
                -
              </Text>
            );
          return (
            <Text size="sm" className={passed === total ? 'text-muted-foreground' : 'text-warning'}>
              {passed}/{total}
            </Text>
          );
        },
        meta: { flex: 0.8 },
      },
      {
        id: 'changes',
        header: 'Changes',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.commitsAnalyzed} commits
          </Text>
        ),
        meta: { flex: 1.2 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Started',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.startedAt
              ? new Date(row.original.startedAt).toLocaleString()
              : new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(row.original.startedAt, row.original.finishedAt)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
    ],
    []
  );

  const handlePageChange = (newPageIndex: number, newPageSize: number) => {
    navigate({
      to: '/releases',
      search: {
        page: newPageIndex + 1,
        pageSize: newPageSize,
        propertyId,
      },
    });
  };

  return (
    <Page>
      <PageHeader
        icon={<GitBranch size={24} weight="fill" />}
        title="Releases"
        description="Release QA runs for your applications"
        actions={
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="md"
              iconLeft={<Plus size={16} />}
              onPress={() => setTriggerDialogOpen(true)}
            >
              New Release Run
            </Button>
          </div>
        }
      />

      {runsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : runsQuery.data && runsQuery.data.runs.length > 0 ? (
        <DataTable
          columns={columns}
          data={runsQuery.data.runs}
          aria-label="Release QA runs table"
          manualPagination
          pageCount={runsQuery.data.pagination.totalPages}
          pageIndex={page - 1}
          pageSize={pageSize}
          onPaginationChange={handlePageChange}
          getRowHref={(run) =>
            buildHref({
              to: '/releases/$releaseId',
              params: { releaseId: run.id },
            })
          }
        />
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <GitBranch size={48} className="mx-auto text-muted-foreground mb-4" />
          <Text className="text-muted-foreground">No release runs yet</Text>
          <Text size="sm" className="text-muted-foreground mt-1">
            Start a release QA run to test code changes before deployment
          </Text>
          <div className="mt-4 flex justify-center">
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Plus size={14} />}
              onPress={() => setTriggerDialogOpen(true)}
            >
              New Release Run
            </Button>
          </div>
        </div>
      )}

      <TriggerReleaseDialog isOpen={triggerDialogOpen} onOpenChange={setTriggerDialogOpen} />
    </Page>
  );
};
